import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/SiteLayout";
import Reveal from "@/components/Reveal";
import FounderCard from "@/components/FounderCard";
import SEO, { aboutPageJsonLd } from "@/components/SEO";
import api from "@/lib/api";

const FOUNDERS_FALLBACK = [
  {
    slug: "james-mccreary",
    name: "James McCreary, LPC-S",
    title: "Co-Founder & CEO",
    bio: "James leads Catalyst's clinical and operational strategy. As a Licensed Professional Counselor Supervisor with 20+ years across behavioral-health programs, he's guided organizations through licensure, accreditation, and operational turnarounds — always keeping the front-line clinician at the center of the work.",
    linkedin: "www.linkedin.com/in/james-mccreary-ms-lpc",
    photo_url: null,
  },
  {
    slug: "jessica-mccreary",
    name: "Jessica McCreary, SHRM-CP",
    title: "Co-Founder & Principal HR Consultant",
    bio: "Jessica architects the people systems behavioral-health organizations rely on — from talent strategy to compliant HR operations. SHRM-CP certified, she partners with growth-stage healthcare teams to build hiring, onboarding, and retention practices that scale without losing culture.",
    linkedin: "https://www.linkedin.com/in/jessica-tappero/",
    photo_url: null,
  },
  {
    slug: "kristin-hale",
    name: "Kristin Hale, LPC-S",
    title: "Co-Founder & Principal Compliance Consultant",
    bio: "Kristin translates the complexity of state, federal, and accreditor requirements into workable, everyday practice. LPC-S with deep experience guiding programs through licensure, CARF and Joint Commission surveys, and payer audits — turning compliance from a burden into a competitive advantage.",
    linkedin: "https://www.linkedin.com/in/kristin-hale/",
    photo_url: null,
  },
];

export default function About() {
  const [founders, setFounders] = useState(FOUNDERS_FALLBACK);

  useEffect(() => {
    let cancelled = false;
    api.get("/founders")
      .then(({ data }) => {
        if (!cancelled && Array.isArray(data) && data.length > 0) setFounders(data);
      })
      .catch(() => { /* fall back to hardcoded */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <SiteLayout branch="main">
      <SEO
        title="About"
        path="/about"
        description="Meet the clinicians and operators behind Catalyst BH Consulting — behavioral-health, HR, and compliance advisors serving programs nationwide."
        jsonLd={aboutPageJsonLd}
      />
      <section className="section bg-[var(--cream)]">
        <Reveal className="max-w-4xl mx-auto">
          <span className="eyebrow-dark">THE CATALYST DIFFERENCE</span>
          <h1 className="font-display mt-5 text-[clamp(40px,5vw,68px)] leading-[1.05] text-[var(--navy)]">
            Strategy that <em className="italic text-[var(--gold)]">reaches the front line.</em>
          </h1>
          <p className="font-display text-2xl leading-[1.5] text-[var(--navy)] mt-8 italic">
            "The strongest organizations do not separate people, clinical quality, compliance, and
            operations. They build systems where each strengthens the others."
          </p>
        </Reveal>
      </section>

      {/* MEET THE FOUNDERS */}
      <section className="section bg-white" data-testid="founders-section">
        <div className="max-w-[1200px] mx-auto">
          <Reveal className="max-w-3xl">
            <span className="eyebrow-dark">MEET THE FOUNDERS</span>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--navy)] mt-4 leading-[1.1]">
              Built by clinicians and operators who've lived it.
            </h2>
            <p className="text-[var(--muted)] text-lg leading-relaxed mt-5">
              Catalyst was founded on a simple conviction — that behavioral-health organizations
              deserve advisors who understand the clinical work, the people who deliver it, and
              the compliance backbone that holds it all together.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8 mt-14 auto-rows-fr" data-testid="founders-grid">
            {founders.map((f, i) => (
              <Reveal key={f.slug || f.name} delay={i * 100}>
                <FounderCard
                  name={f.name}
                  title={f.title}
                  bio={f.bio}
                  photo={f.photo_url}
                  linkedin={f.linkedin}
                  testId={`founder-${i}`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="grid lg:grid-cols-2 gap-16 max-w-[1200px] mx-auto">
          <Reveal>
            <h2 className="font-display text-3xl text-[var(--navy)] mb-6">What makes us different</h2>
            <ul className="space-y-4 text-[var(--muted)] leading-relaxed">
              <li>· Behavioral health <strong className="text-[var(--navy)]">and</strong> HR expertise inside one firm</li>
              <li>· Solutions tailored to your organization's stage and market</li>
              <li>· Tools, training, and workflows built for real-world use</li>
              <li>· Project, retainer, fractional, and digital support models</li>
              <li>· 20+ years serving behavioral healthcare organizations nationwide</li>
            </ul>
          </Reveal>
          <Reveal>
            <h2 className="font-display text-3xl text-[var(--navy)] mb-6">How we engage</h2>
            <div className="space-y-6 text-[var(--muted)]">
              <div>
                <div className="text-[var(--gold)] text-[10px] tracking-widest font-bold mb-1">DISCOVERY</div>
                <p>A no-pressure 30-minute call to explore your goals and constraints.</p>
              </div>
              <div>
                <div className="text-[var(--gold)] text-[10px] tracking-widest font-bold mb-1">DIAGNOSTIC</div>
                <p>A right-sized assessment to identify highest-value opportunities.</p>
              </div>
              <div>
                <div className="text-[var(--gold)] text-[10px] tracking-widest font-bold mb-1">ENGAGEMENT</div>
                <p>Project, retainer, fractional, or digital-first — whatever fits your stage.</p>
              </div>
            </div>
            <Link to="/book" className="btn btn-navy mt-8 inline-flex">Book Discovery Call</Link>
          </Reveal>
        </div>
      </section>
    </SiteLayout>
  );
}
